import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import Axios from "axios";
import { AppState } from "reducers";
import { ReduxActionTypes } from "constants/ReduxActionConstants";
import { sendUserLogs } from "api/userLogs";

// ── AI Agents that orbit the Brain ──────────────────────────────────────────
const AGENTS = [
  { icon: "💼", label: "Sales Agent" },
  { icon: "🤝", label: "CRM Agent" },
  { icon: "👥", label: "HR Agent" },
  { icon: "💰", label: "Finance Agent" },
  { icon: "🎧", label: "Support Agent" },
  { icon: "📣", label: "Marketing Agent" },
  { icon: "⚖️", label: "Legal Agent" },
  { icon: "📊", label: "Analytics Agent" },
];

// ── Integrations showcase ─────────────────────────────────────────────────────
const FAV = (domain: string) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

const INTEGRATIONS = [
  {
    category: "Messaging",
    items: [
      {
        logo: FAV("slack.com"),
        name: "Slack",
        desc:
          "Chat with your Brain and get answers right inside your Slack channels.",
      },
      {
        logo: FAV("whatsapp.com"),
        name: "WhatsApp",
        desc: "Message your Brain from WhatsApp — it replies like a teammate.",
      },
      {
        logo: FAV("telegram.org"),
        name: "Telegram",
        desc: "Talk to your Brain through a Telegram bot, anywhere you go.",
      },
      {
        logo: FAV("discord.com"),
        name: "Discord",
        desc: "Bring your Brain into Discord servers, channels and DMs.",
      },
      {
        logo: FAV("teams.microsoft.com"),
        name: "Teams",
        desc: "Collaborate with your Brain directly inside Microsoft Teams.",
      },
    ],
  },
  {
    category: "AI Models",
    items: [
      {
        logo: FAV("claude.ai"),
        name: "Claude",
        desc: "Anthropic's Claude models for deep reasoning and long context.",
      },
      {
        logo: FAV("openai.com"),
        name: "GPT-4o",
        desc: "OpenAI's flagship model for fast, versatile conversation.",
      },
      {
        logo: FAV("gemini.google.com"),
        name: "Gemini",
        desc: "Google's Gemini models with strong multimodal understanding.",
      },
      {
        logo: FAV("x.ai"),
        name: "Grok",
        desc: "xAI's Grok with real-time knowledge and a bold personality.",
      },
      {
        logo: FAV("mistral.ai"),
        name: "Mistral",
        desc: "Fast, open-weight European models — great value for scale.",
      },
    ],
  },
  {
    category: "Knowledge & tools",
    items: [
      {
        logo: FAV("notion.so"),
        name: "Notion",
        desc: "Let your Brain read and update your Notion workspace.",
      },
      {
        logo: FAV("github.com"),
        name: "GitHub",
        desc: "Give your Brain access to repos, issues and pull requests.",
      },
      {
        logo: FAV("docs.google.com"),
        name: "Google Docs",
        desc: "Draft, summarise and edit documents in Google Docs.",
      },
      {
        logo: FAV("gmail.com"),
        name: "Email",
        desc: "Your Brain reads, drafts and sends email on your behalf.",
      },
      {
        logo: FAV("obsidian.md"),
        name: "Obsidian",
        desc: "Connect your Obsidian vault as long-term company memory.",
      },
    ],
  },
];

// Precomputed polar layout: sphere sits at the centre (50,50); each category
// occupies its own angular sector so integrations are grouped visually.
const NODE_RADIUS = 32; // % of stage — distance of logo bubbles from centre
const GROUP_SPREAD = 52; // ° — arc each category spans (smaller = tighter clusters)
const LABEL_GAP = 9; // % — clear space between a cluster and its label
const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));
const ORBIT = INTEGRATIONS.map((group, gi) => {
  const centerAngle = 90 + gi * (360 / INTEGRATIONS.length); // 90°, 210°, 330°
  const n = group.items.length;
  const items = group.items.map((item, ii) => {
    const angle =
      n === 1
        ? centerAngle
        : centerAngle - GROUP_SPREAD / 2 + (ii / (n - 1)) * GROUP_SPREAD;
    const rad = (angle * Math.PI) / 180;
    return {
      ...item,
      category: group.category,
      x: 50 + Math.cos(rad) * NODE_RADIUS,
      y: 50 - Math.sin(rad) * NODE_RADIUS,
    };
  });

  // Place the label clear of the whole cluster: above it for the top group,
  // below it for the lower groups — never radially behind a node.
  const centroidX = items.reduce((a, it) => a + it.x, 0) / n;
  const minY = items.reduce((m, it) => Math.min(m, it.y), Infinity);
  const maxY = items.reduce((m, it) => Math.max(m, it.y), -Infinity);
  const isUpper = Math.sin((centerAngle * Math.PI) / 180) >= 0;
  const labelY = isUpper ? minY - LABEL_GAP : maxY + LABEL_GAP;

  return {
    category: group.category,
    labelX: clamp(centroidX, 18, 82),
    labelY,
    items,
  };
});

// ── Hardware tiers ────────────────────────────────────────────────────────────

// ── Hardware tiers ────────────────────────────────────────────────────────────
interface HardwareTier {
  key: string;
  name: string;
  priceMonth: number;
  ram: string;
  cpu: string;
  tagline: string;
  capabilities: string[];
  recommended?: boolean;
}

const HARDWARE_TIERS: HardwareTier[] = [
  {
    key: "starter",
    name: "Starter",
    priceMonth: 39,
    ram: "8 GB",
    cpu: "4 vCPUs",
    tagline: "For small teams getting started",
    capabilities: [
      "Up to 5 active users",
      "3 agents in parallel",
      "Runs 3 tools alongside",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    priceMonth: 79,
    ram: "16 GB",
    cpu: "8 vCPUs",
    tagline: "For growing teams that move fast",
    capabilities: [
      "Up to 25 active users",
      "8 agents in parallel",
      "Runs 8 tools alongside",
    ],
    recommended: true,
  },
  {
    key: "business",
    name: "Business",
    priceMonth: 119,
    ram: "32 GB",
    cpu: "16 vCPUs",
    tagline: "For large teams that need it all",
    capabilities: [
      "100+ active users",
      "20+ agents in parallel",
      "Runs 20+ tools alongside",
    ],
  },
];

// ── Available services catalog ───────────────────────────────────────────────
type ServiceCategory =
  | "Frontend"
  | "Backend"
  | "Database"
  | "AI Agent"
  | "AI"
  | "Other";

interface ServiceOption {
  key: string;
  icon: string;
  name: string;
  category: ServiceCategory;
  desc: string;
}

const CATEGORY_COLOR: Record<ServiceCategory, { bg: string; text: string }> = {
  Frontend: { bg: "#e6f4ff", text: "#0958d9" },
  Backend: { bg: "#fff7e6", text: "#d46b08" },
  Database: { bg: "#f0f5ff", text: "#2f54eb" },
  "AI Agent": { bg: "#f9f0ff", text: "#722ed1" },
  AI: { bg: "#f9f0ff", text: "#722ed1" },
  Other: { bg: "#f6ffed", text: "#389e0d" },
};

const SERVICES: ServiceOption[] = [
  {
    key: "nodered",
    icon: "🔴",
    name: "Node Red",
    category: "Backend",
    desc: "Back-end applications using visual blocks.",
  },
  {
    key: "ui-editor",
    icon: "🖌️",
    name: "UI Editor",
    category: "Frontend",
    desc: "UBOS UI – web user interface builder.",
  },
  {
    key: "ai-bot",
    icon: "🤖",
    name: "AI Bot Starter Kit",
    category: "AI Agent",
    desc: "Build a custom AI chatbot for any business.",
  },
  {
    key: "nextchat",
    icon: "💬",
    name: "NextChat",
    category: "Frontend",
    desc: "Versatile AI chat companion, compatible with leading LLMs.",
  },
  {
    key: "open-webui",
    icon: "🌐",
    name: "Open WebUI",
    category: "AI",
    desc: "Friendly, self-hosted web interface for AI models.",
  },
  {
    key: "librechat",
    icon: "🗨️",
    name: "LibreChat",
    category: "AI",
    desc: "Step into the chat room of tomorrow with LibreChat.",
  },
  {
    key: "langfuse",
    icon: "📊",
    name: "Langfuse",
    category: "AI",
    desc: "Open-source LLM engineering & observability platform.",
  },
  {
    key: "litellm",
    icon: "⚡",
    name: "LiteLLM",
    category: "AI",
    desc: "Call all LLM APIs using the OpenAI format.",
  },
  {
    key: "postgresql",
    icon: "🐘",
    name: "PostgreSQL",
    category: "Database",
    desc: "Object-relational database system.",
  },
  {
    key: "mysql",
    icon: "🗄️",
    name: "MySQL",
    category: "Database",
    desc: "Relational database management system.",
  },
  {
    key: "mongodb",
    icon: "🍃",
    name: "Mongo DB",
    category: "Database",
    desc: "Non-relational document database.",
  },
  {
    key: "chromadb",
    icon: "🔮",
    name: "Chroma DB",
    category: "Database",
    desc: "Open-source vector storage system.",
  },
  {
    key: "baserow",
    icon: "📋",
    name: "Baserow",
    category: "Database",
    desc: "Create scalable databases and applications without code.",
  },
  {
    key: "dbeaver",
    icon: "🦫",
    name: "DBeaver",
    category: "Other",
    desc: "Universal database tool.",
  },
  {
    key: "tooljet",
    icon: "🛠️",
    name: "ToolJet",
    category: "Frontend",
    desc: "The superhero of low-code frameworks.",
  },
  {
    key: "openui",
    icon: "✨",
    name: "OpenUI",
    category: "Frontend",
    desc: "A magic wand for building UI components.",
  },
  {
    key: "keycloak",
    icon: "🔐",
    name: "Keycloak",
    category: "Other",
    desc: "Unlock the magic of seamless authentication.",
  },
  {
    key: "grafana",
    icon: "📈",
    name: "Grafana",
    category: "Other",
    desc: "Unleash the power of your data with Grafana.",
  },
  {
    key: "jupyterhub",
    icon: "📓",
    name: "JupyterHub",
    category: "Other",
    desc: "Serve Jupyter notebooks for multiple users.",
  },
  {
    key: "chatwoot",
    icon: "💁",
    name: "Chatwoot",
    category: "Other",
    desc: "Open-source customer engagement suite.",
  },
  {
    key: "bytebase",
    icon: "🗃️",
    name: "Bytebase",
    category: "Other",
    desc: "Database schema change and version control for DevOps.",
  },
  {
    key: "theia",
    icon: "☁️",
    name: "Theia",
    category: "Other",
    desc: "Cloud IDE for development in the browser.",
  },
  {
    key: "mqtt",
    icon: "📡",
    name: "MQTT",
    category: "Other",
    desc: "Eclipse Mosquitto message broker.",
  },
  {
    key: "ubos-chat",
    icon: "💙",
    name: "UBOS Chat",
    category: "Other",
    desc: "Open-source customer engagement suite.",
  },
];

// ── Styled components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: radial-gradient(
      ellipse 90% 60% at 8% 15%,
      rgba(99, 102, 241, 0.13) 0%,
      transparent 55%
    ),
    radial-gradient(
      ellipse 70% 70% at 88% 8%,
      rgba(14, 165, 233, 0.1) 0%,
      transparent 55%
    ),
    radial-gradient(
      ellipse 80% 50% at 55% 92%,
      rgba(168, 85, 247, 0.09) 0%,
      transparent 55%
    ),
    #f5f6ff;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.2) transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.25);
    border-radius: 99px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const HeroPanel = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
`;

const GlassCard = styled.div`
  width: 100%;
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* Left pitch column */
const PitchColumn = styled.div`
  position: relative;
  padding: 64px clamp(40px, 6vw, 96px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: radial-gradient(
      ellipse 120% 90% at 0% 0%,
      rgba(99, 102, 241, 0.16) 0%,
      transparent 60%
    ),
    radial-gradient(
      ellipse 100% 80% at 100% 100%,
      rgba(168, 85, 247, 0.12) 0%,
      transparent 60%
    ),
    linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  border-right: 1px solid rgba(255, 255, 255, 0.6);

  @media (max-width: 900px) {
    padding: 48px 36px 16px;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.6);
    text-align: center;
    align-items: center;
  }
`;

/* Right config column */
const ConfigColumn = styled.div`
  padding: 56px clamp(28px, 3.5vw, 64px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;

  @media (max-width: 900px) {
    padding: 40px 36px 48px;
    max-width: none;
  }

  @media (max-width: 480px) {
    padding: 28px 16px 36px;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(30px, 3.4vw, 46px);
  font-weight: 800;
  color: #0f172a;
  line-height: 1.12;
  margin: 0 0 20px;
  letter-spacing: -0.03em;
`;

const PitchLead = styled.p`
  font-size: 15px;
  color: #475569;
  line-height: 1.7;
  margin: 0 0 28px;
  max-width: 420px;
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #4f46e5 0%, #0ea5e9 55%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

/* ─── Minimal particle-sphere Brain ─── */
const nodePulse = keyframes`
  0%, 100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(129,140,248,0.5); }
  50%      { transform: scale(1.3); box-shadow: 0 0 0 6px rgba(129,140,248,0); }
`;
const wakeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const haloPulse = keyframes`
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
  50%      { opacity: 0.75; transform: translate(-50%, -50%) scale(1.06); }
`;

const BrainStage = styled.div<{ $intensity: number }>`
  position: relative;
  width: 100%;
  height: clamp(280px, 34vh, 380px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0 20px;
`;

/* soft central halo behind the particle sphere */
const SphereHalo = styled.div<{ $intensity: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${p => 220 + p.$intensity * 26}px;
  height: ${p => 220 + p.$intensity * 26}px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, ${p => 0.16 + p.$intensity * 0.04}) 0%,
    rgba(14, 165, 233, 0.08) 45%,
    transparent 70%
  );
  filter: blur(10px);
  animation: ${haloPulse} ${p => 5 - p.$intensity * 0.5}s ease-in-out infinite;
  pointer-events: none;
`;

const SphereCanvas = styled.canvas`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: block;
`;

/* ─── Integration constellation around the Brain ─── */
const orbitFloat = keyframes`
  0%, 100% { transform: translate(-50%, -50%) translateY(0); }
  50%      { transform: translate(-50%, -50%) translateY(-6px); }
`;

const tipIn = keyframes`
  from { opacity: 0; transform: translate(-50%, 4px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

const OrbitStage = styled.div`
  position: relative;
  width: min(100%, 480px);
  aspect-ratio: 1 / 1;
  margin: 12px auto 18px;

  @media (max-width: 480px) {
    width: min(100%, 360px);
  }
`;

const OrbitLines = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const OrbitCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 52%;
  height: 52%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OrbitNode = styled.button<{ $dim: boolean }>`
  position: absolute;
  transform: translate(-50%, -50%);
  width: 43px;
  height: 43px;
  padding: 0;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid
    ${p => (p.$dim ? "rgba(226, 232, 240, 0.9)" : "rgba(203, 213, 225, 0.9)")};
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  animation: ${orbitFloat} 4s ease-in-out infinite;
  transition: box-shadow 0.18s, border-color 0.18s, transform 0.18s,
    opacity 0.18s;
  opacity: ${p => (p.$dim ? 0.4 : 1)};

  img {
    width: 22px;
    height: 22px;
    border-radius: 5px;
    display: block;
  }

  &:hover,
  &:focus-visible {
    outline: none;
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.18);
    box-shadow: 0 8px 24px rgba(79, 70, 229, 0.28);
    border-color: #4f46e5;
    animation-play-state: paused;
    z-index: 6;
  }

  @media (max-width: 480px) {
    width: 34px;
    height: 34px;
    img {
      width: 17px;
      height: 17px;
    }
  }
`;

const OrbitLabel = styled.div<{ $active: boolean }>`
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 99px;
  padding: 5px 12px;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
  transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
  transform: ${p =>
    p.$active
      ? "translate(-50%, -50%) scale(1.08)"
      : "translate(-50%, -50%) scale(1)"};
  border-color: ${p =>
    p.$active ? "rgba(79, 70, 229, 0.5)" : "rgba(203, 213, 225, 0.9)"};
`;

const OrbitTooltip = styled.div`
  position: absolute;
  transform: translate(-50%, 0);
  z-index: 10;
  width: 210px;
  padding: 12px 14px;
  background: #0f172a;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.32);
  pointer-events: none;
  animation: ${tipIn} 0.14s ease both;

  .tip-head {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 5px;
  }
  .tip-head img {
    width: 16px;
    height: 16px;
    border-radius: 4px;
  }
  .tip-name {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
  }
  .tip-cat {
    margin-left: auto;
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .tip-desc {
    font-size: 11.5px;
    line-height: 1.45;
    color: #cbd5e1;
  }

  &::after {
    content: "";
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: #0f172a;
    border-radius: 2px;
  }
`;

const BrainStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  margin-bottom: 22px;
  background: rgba(15, 23, 42, 0.03);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 99px;
  padding: 7px 15px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  font-variant-numeric: tabular-nums;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5);
    animation: ${nodePulse} 1.8s ease-in-out infinite;
  }
  .slug {
    color: #4f46e5;
    font-weight: 700;
  }
  .ready {
    color: #16a34a;
    font-weight: 700;
  }

  @media (max-width: 900px) {
    align-self: center;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;
    justify-content: center;
    text-align: center;
    gap: 6px 8px;
    padding: 7px 12px;
  }
`;

/* ─── Boot / ignition sequence ─── */
const bootOrbIgnite = keyframes`
  0%   { transform: scale(0.7); filter: brightness(0.8); }
  60%  { transform: scale(1.15); filter: brightness(1.4); }
  100% { transform: scale(1); filter: brightness(1.15); }
`;
const lineIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const BootOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 26px;
  background: radial-gradient(
      ellipse 70% 60% at 50% 40%,
      rgba(49, 46, 129, 0.55) 0%,
      transparent 70%
    ),
    linear-gradient(160deg, #0b1020 0%, #131a35 100%);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: ${wakeIn} 0.35s ease both;
`;

const BootOrb = styled.div`
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: radial-gradient(
      circle at 34% 30%,
      rgba(255, 255, 255, 0.95) 0%,
      transparent 34%
    ),
    linear-gradient(150deg, #6366f1 0%, #0ea5e9 55%, #a855f7 100%);
  box-shadow: 0 0 60px rgba(99, 102, 241, 0.8),
    0 0 120px rgba(14, 165, 233, 0.5), inset 0 -8px 26px rgba(30, 27, 75, 0.5);
  animation: ${bootOrbIgnite} 1.4s ease-in-out infinite alternate;
`;

const BootTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #fff;
  text-align: center;

  .slug {
    color: #a5b4fc;
  }
`;

const BootLog = styled.div`
  width: min(340px, 80%);
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const BootLine = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  font-family: ui-monospace, "SF Mono", monospace;
  color: ${p => (p.$active ? "#e0e7ff" : "#7c8bb8")};
  animation: ${lineIn} 0.3s ease both;

  .mark {
    width: 16px;
    text-align: center;
    color: ${p => (p.$active ? "#818cf8" : "#22c55e")};
  }
`;

const FormDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;

  .label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;
  }

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(148, 163, 184, 0.3);
  }
`;

const FieldLabel = styled.label`
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 7px;
  display: block;
`;

const NameInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.75);
  border: 1.5px solid rgba(203, 213, 225, 0.7);
  border-radius: 12px;
  padding: 11px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  letter-spacing: -0.01em;

  &:focus {
    border-color: rgba(79, 70, 229, 0.5);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }
  &::placeholder {
    color: #94a3b8;
  }
`;

const UrlHint = styled.div`
  margin: 6px 0 4px;
  font-size: 11.5px;
  font-family: ui-monospace, "SF Mono", monospace;
  background: rgba(241, 245, 249, 0.8);
  border-radius: 8px;
  padding: 6px 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .prefix {
    color: #94a3b8;
  }
  .slug {
    color: #4f46e5;
    font-weight: 600;
  }
`;

const TiersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 26px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const TierCard = styled.div<{ $selected: boolean }>`
  position: relative;
  border-radius: 18px;
  padding: 18px 16px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  border: 1.5px solid
    ${p => (p.$selected ? "#4f46e5" : "rgba(203,213,225,0.6)")};
  background: ${p =>
    p.$selected
      ? "linear-gradient(160deg, rgba(79,70,229,0.09) 0%, rgba(14,165,233,0.06) 100%)"
      : "rgba(255,255,255,0.7)"};
  box-shadow: ${p =>
    p.$selected
      ? "0 0 0 4px rgba(79,70,229,0.12), 0 12px 28px rgba(79,70,229,0.16)"
      : "0 2px 10px rgba(15,23,42,0.05)"};
  transition: border-color 0.16s, background 0.16s, box-shadow 0.16s,
    transform 0.14s;
  margin-top: 10px;

  &:hover {
    border-color: ${p => (p.$selected ? "#4f46e5" : "rgba(99,102,241,0.45)")};
    transform: translateY(-3px);
    box-shadow: ${p =>
      p.$selected
        ? "0 0 0 4px rgba(79,70,229,0.14), 0 16px 34px rgba(79,70,229,0.2)"
        : "0 10px 24px rgba(15,23,42,0.1)"};
  }
  &:focus-visible {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.18);
  }

  /* Selection check */
  .tier-check {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: #fff;
    background: ${p =>
      p.$selected
        ? "linear-gradient(135deg, #4f46e5, #0ea5e9)"
        : "transparent"};
    border: ${p =>
      p.$selected ? "none" : "1.5px solid rgba(203,213,225,0.9)"};
    transition: all 0.16s;
  }

  .tier-name {
    font-size: 14px;
    font-weight: 800;
    color: ${p => (p.$selected ? "#4f46e5" : "#0f172a")};
    letter-spacing: -0.02em;
    margin-bottom: 3px;
    white-space: nowrap;
  }
  .tier-tagline {
    font-size: 11.5px;
    color: #94a3b8;
    line-height: 1.3;
    margin-bottom: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tier-price {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 2px;
    margin-bottom: 14px;
  }
  .tier-price .amount {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${p => (p.$selected ? "#4f46e5" : "#0f172a")};
    font-variant-numeric: tabular-nums;
  }
  .tier-price .unit {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
  }
  .tier-price .sub {
    flex-basis: 100%;
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
    margin-top: 1px;
  }
  /* Brain capabilities */
  .tier-caps {
    list-style: none;
    margin: 0 0 14px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .tier-caps li {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11.5px;
    font-weight: 600;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tier-caps li::before {
    content: "";
    flex: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${p =>
      p.$selected
        ? "linear-gradient(135deg, #4f46e5, #0ea5e9)"
        : "rgba(148,163,184,0.28)"};
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
    background-image: ${p =>
      p.$selected ? "linear-gradient(135deg, #4f46e5, #0ea5e9)" : "none"};
  }
  .tier-specs {
    display: flex;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid
      ${p => (p.$selected ? "rgba(79,70,229,0.14)" : "rgba(203,213,225,0.5)")};
  }
  .tier-spec {
    flex: 1;
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }
  .tier-spec strong {
    display: block;
    font-size: 9px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 16px;
  background: linear-gradient(90deg, #4f46e5, #0ea5e9);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 99px;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
`;

const SetupButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #4f46e5 0%, #0ea5e9 100%);
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 18px 24px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(79, 70, 229, 0.36),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
  transition: filter 0.15s, transform 0.15s, box-shadow 0.15s;
  margin-bottom: 12px;

  /* Sheen sweep */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -60%;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      100deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    transform: skewX(-20deg);
    transition: left 0.5s ease;
  }

  &:hover {
    filter: brightness(1.07);
    transform: translateY(-2px);
    box-shadow: 0 18px 44px rgba(79, 70, 229, 0.46);
  }
  &:hover::after {
    left: 130%;
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 6px 18px rgba(79, 70, 229, 0.34);
  }
  &:disabled {
    cursor: default;
    filter: saturate(0.8);
    opacity: 0.85;
  }
`;

const PayNote = styled.p`
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

/* ─── "Brain already live" state (org already has a server) ─── */
const LiveCard = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px clamp(32px, 6vw, 96px);
  background: radial-gradient(
      ellipse 100% 80% at 50% 0%,
      rgba(99, 102, 241, 0.14) 0%,
      transparent 60%
    ),
    radial-gradient(
      ellipse 90% 70% at 50% 100%,
      rgba(168, 85, 247, 0.1) 0%,
      transparent 60%
    );
`;

const LiveActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
`;

const LivePrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #4f46e5 0%, #0ea5e9 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 14px 26px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(79, 70, 229, 0.32);
  transition: filter 0.15s, transform 0.15s;

  &:hover {
    filter: brightness(1.07);
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.6);
  color: #4f46e5;
  border: 1px solid rgba(99, 102, 241, 0.28);
  border-radius: 14px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
`;

const CloseHint = styled.button`
  margin-top: 22px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #475569;
    text-decoration: underline;
  }
`;

/* "Every Brain includes" value block — the OpenClaw + Hermes engine */
const IncludedBlock = styled.div`
  margin-top: 22px;
  max-width: 680px;
  width: 100%;
  padding: 18px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(99, 102, 241, 0.14);
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  backdrop-filter: blur(8px);

  .inc-head {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 12px;
  }
  .inc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 18px;

    @media (max-width: 520px) {
      grid-template-columns: 1fr;
    }
  }
  .inc-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    line-height: 1.35;
  }
  .inc-item .ic {
    flex: none;
    font-size: 14px;
    width: 18px;
    text-align: center;
  }

  @media (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
    text-align: left;
  }
`;

const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 16px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;

  .pb-lead {
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #cbd5e1;
  }
  .pb-item {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
  .pb-item img {
    height: 18px;
    width: auto;
    display: block;
    opacity: 0.9;
  }
  .pb-item .pb-name {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }
  .pb-item strong {
    color: #475569;
    font-weight: 700;
    font-size: 12px;
  }
  .pb-item .pb-sub {
    color: #94a3b8;
    font-weight: 500;
    font-size: 10px;
  }
  .sep {
    color: #e2e8f0;
  }
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  color: #94a3b8;
  font-size: 11px;
  letter-spacing: 0.04em;
  font-weight: 500;
  user-select: none;
  pointer-events: none;

  .arrow {
    font-size: 16px;
    line-height: 1;
    animation: bobDown 1.8s ease-in-out infinite;
  }

  @keyframes bobDown {
    0%,
    100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    50% {
      transform: translateY(5px);
      opacity: 1;
    }
  }
`;

/* ─── Ecosystem teaser ─── */
const ToolsPanel = styled.div`
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
  padding: 40px 24px 56px;
  box-sizing: border-box;
`;

const TeaserHead = styled.div`
  text-align: center;
  margin-bottom: 26px;

  .kicker {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 8px;
  }
  .title {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
  }
`;

const TeaserGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TeaserCard = styled.div`
  position: relative;
  text-align: left;
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 20px;
  padding: 22px 24px 20px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.06);
  transition: transform 0.16s, box-shadow 0.16s, border-color 0.16s;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(99, 102, 241, 0.35);
    box-shadow: 0 18px 44px rgba(79, 70, 229, 0.16);
  }
  &:focus-visible {
    outline: 2px solid rgba(79, 70, 229, 0.5);
    outline-offset: 2px;
  }

  .t-title {
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin-bottom: 5px;
  }
  .t-desc {
    font-size: 12.5px;
    line-height: 1.55;
    color: #64748b;
    margin-bottom: 16px;
  }
  .t-cta {
    font-size: 13px;
    font-weight: 700;
    color: #4f46e5;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: gap 0.16s;
  }
  &:hover .t-cta {
    gap: 9px;
  }
`;

const TeaserIconRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  .bubble {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    line-height: 1;
    margin-left: -10px;

    &:first-child {
      margin-left: 0;
    }
  }
  .count {
    margin-left: 10px;
    font-size: 12px;
    font-weight: 700;
    color: #4f46e5;
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 99px;
    padding: 4px 11px;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────

/**
 * Rotating 3D particle sphere rendered on canvas.
 * Points sit on a Fibonacci sphere; nearby points are linked with faint lines.
 * Connections are precomputed once (rigid rotation keeps neighbours constant).
 */
const BrainSphere: React.FC<{ intensity: number }> = ({ intensity }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // ── build a Fibonacci sphere of points ──
    const N = 240;
    const golden = Math.PI * (3 - Math.sqrt(5));
    const seeded = (s: number) => {
      const x = Math.sin(s * 127.1) * 43758.5453;
      return x - Math.floor(x);
    };
    const points = Array.from({ length: N }, (_, i) => {
      const y = 1 - (i / (N - 1)) * 2;
      const rad = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = golden * i;
      return {
        x: Math.cos(theta) * rad,
        y,
        z: Math.sin(theta) * rad,
        accent: seeded(i + 1) > 0.8,
        r: 0.7 + seeded(i + 2) * 1.5,
      };
    });

    // bigger server → larger orb & denser connections
    const radiusScale = 0.78 + intensity * 0.11; // 0.89 / 1.00 / 1.11
    const maxDist = 0.27 + intensity * 0.055; // 0.325 / 0.38 / 0.435

    // ── precompute nearby links (constant under rigid rotation) ──
    const links: Array<[number, number]> = [];
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const dx = points[i].x - points[j].x;
        const dy = points[i].y - points[j].y;
        const dz = points[i].z - points[j].z;
        if (dx * dx + dy * dy + dz * dz < maxDist * maxDist) links.push([i, j]);
      }
    }

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    ro?.observe(canvas);
    window.addEventListener("resize", resize);

    let raf = 0;
    let angle = 0;
    const tilt = 0.5;
    const cosT = Math.cos(tilt);
    const sinT = Math.sin(tilt);
    const speed = 0.0015 + intensity * 0.0006;
    const fov = 3.4;

    const projected = new Array(N);

    const frame = () => {
      angle += speed;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const cx = width / 2;
      const cy = height / 2;
      const sphereR = Math.min(width, height) * 0.42 * radiusScale;

      for (let i = 0; i < N; i++) {
        const p = points[i];
        const rx = p.x * cosA - p.z * sinA;
        const rz = p.x * sinA + p.z * cosA;
        const ry = p.y * cosT - rz * sinT;
        const rz2 = p.y * sinT + rz * cosT;
        const persp = fov / (fov - rz2);
        projected[i] = {
          sx: cx + rx * sphereR * persp,
          sy: cy + ry * sphereR * persp,
          depth: (rz2 + 1) / 2,
          persp,
          r: p.r,
          accent: p.accent,
        };
      }

      ctx.clearRect(0, 0, width, height);

      // links
      ctx.lineWidth = 0.6;
      for (let k = 0; k < links.length; k++) {
        const a = projected[links[k][0]];
        const b = projected[links[k][1]];
        const depth = (a.depth + b.depth) / 2;
        const alpha = (depth - 0.12) * 0.3;
        if (alpha <= 0.015) continue;
        ctx.strokeStyle = `rgba(99,102,241,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(a.sx, a.sy);
        ctx.lineTo(b.sx, b.sy);
        ctx.stroke();
      }

      // points
      for (let i = 0; i < N; i++) {
        const p = projected[i];
        const size = p.r * (0.5 + p.depth) * p.persp;
        const alpha = 0.2 + p.depth * 0.8;
        if (p.accent) {
          ctx.fillStyle = `rgba(14,165,233,${alpha})`;
          ctx.shadowColor = "rgba(14,165,233,0.85)";
          ctx.shadowBlur = 6 * p.depth;
        } else {
          ctx.fillStyle = `rgba(79,70,229,${alpha * 0.85})`;
          ctx.shadowBlur = 0;
        }
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      if (!reduced) raf = requestAnimationFrame(frame);
    };

    frame();

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [intensity]);

  return <SphereCanvas ref={canvasRef} />;
};

const OnboardingPage: React.FC = () => {
  const dispatch = useDispatch();

  const currentOrg = useSelector((state: AppState) => state.ui.orgs.currentOrg);
  const curApplicationID = useSelector(
    (state: AppState) => state.ui.applications?.curApplication,
  );
  const user = useSelector((state: AppState) => state.ui.users.currentUser);

  // When the org already has a running server, its Company Brain is live —
  // switch the onboarding tab from "create a Brain" to a "your Brain is live" state.
  const hasBrain = Boolean(currentOrg?.server);
  const brainName = currentOrg?.name || "your Company Brain";

  const defaultName = useMemo(
    () => `company-brain-${Math.floor(1000 + Math.random() * 9000)}`,
    [],
  );
  const [serviceName, setServiceName] = useState(defaultName);
  const [selectedTier, setSelectedTier] = useState("pro");
  const [phase, setPhase] = useState<"idle" | "redirecting" | "installing">(
    "idle",
  );
  const [bootStep, setBootStep] = useState(0);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const booting = phase === "installing";

  const selectedTierData =
    HARDWARE_TIERS.find(t => t.key === selectedTier) ?? HARDWARE_TIERS[0];
  const tierIndex = HARDWARE_TIERS.findIndex(t => t.key === selectedTier);
  const intensity = tierIndex < 0 ? 1 : tierIndex + 1; // 1..3 — drives orb energy

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sanitize: lowercase, alphanumeric + hyphens, max 40 chars
    const sanitized = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "")
      .slice(0, 40);
    setServiceName(sanitized);
  };

  const displayName = serviceName || defaultName;

  const bootLines = useMemo(
    () => [
      `Allocating ${selectedTierData.name} Brain · ${selectedTierData.ram} · ${selectedTierData.cpu}`,
      "Waking the Brain core",
      "Loading your company memory",
      "Connecting to your tools & data",
      "Calibrating workflows & alerts",
      "Company Brain is online",
    ],
    [selectedTierData],
  );

  // Boot sequence: step through the log, then open the Logs tab where the
  // real provisioning (server + service, driven by the Stripe webhook) is shown
  useEffect(() => {
    if (phase !== "installing") return;
    if (bootStep < bootLines.length) {
      const t = setTimeout(
        () => setBootStep(s => s + 1),
        bootStep === 0 ? 500 : 650,
      );
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      openLogsTab();
      setPhase("idle");
      setBootStep(0);
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, bootStep, bootLines.length]);

  // Same "Logs" tab as SideNavigation's openLoad()
  const openLogsTab = () => {
    dispatch({
      type: ReduxActionTypes.SET_LAYOUT_ITEM,
      payload: {
        id: JSON.stringify({ id: "Logs", type: "Logs" }),
        component: "UserSettings",
        title: "Logs",
        props: { navigateTo: "Load" },
      },
    });
  };

  const openServiceCreation = (
    title: string,
    extraParams: Record<string, unknown> = {},
  ) => {
    const baseQueryParams = {
      org: currentOrg?.id,
      orgId: currentOrg?.id,
      app: curApplicationID,
      folderId: undefined,
      userId: user?.id,
      userEmail: user?.email,
      ...extraParams,
    };

    const newItem = currentOrg?.server
      ? {
          component: "Tab",
          type: "Tab",
          title,
          icon: "https://ubos.tech/wp-content/uploads/2023/09/ubos-logo-sm.png",
          props: {
            baseUrl: `https://toolmanager.${location.host}/`,
            navigateTo: "Service%20creation",
            queryParams: baseQueryParams,
          },
        }
      : {
          component: "Tab",
          type: "Tab",
          title: "Hardware",
          icon: "https://ubos.tech/wp-content/uploads/2023/09/ubos-logo-sm.png",
          props: {
            baseUrl: `https://provider.${location.host}/`,
            navigateTo: "choose%20provider",
            queryParams: baseQueryParams,
          },
        };

    dispatch({ type: ReduxActionTypes.SET_LAYOUT_ITEM, payload: newItem });
  };

  // Returning from Stripe Checkout: success → run the installation sequence
  // with the paid-for name/tier, cancel → just clean the URL and stay idle.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get("brainCheckout");
    if (!checkoutResult) return;

    if (checkoutResult === "success") {
      const paidName = params.get("serviceName");
      const paidTier = params.get("hardware");
      if (paidName) setServiceName(paidName);
      if (paidTier && HARDWARE_TIERS.some(t => t.key === paidTier)) {
        setSelectedTier(paidTier);
      }
      setBootStep(0);
      setPhase("installing");
    }

    // Strip checkout params so a refresh doesn't replay the flow
    [
      "brainCheckout",
      "session_id",
      "serviceName",
      "hardware",
      "tool",
    ].forEach(key => params.delete(key));
    const rest = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${rest ? `?${rest}` : ""}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetupBrain = async () => {
    if (phase !== "idle") return;
    setPhase("redirecting");

    // Open the tab synchronously inside the click gesture — a window.open
    // after the await would be killed by the popup blocker.
    const checkoutTab = window.open("", "_blank");

    try {
      const { data } = await Axios.post(`/api/createBrainCheckout`, {
        id: user?.id,
        email: user?.email,
        orgId: currentOrg?.id,
        app: curApplicationID,
        server: currentOrg?.server,
        tool: "openclaw",
        serviceName: displayName,
        hardware: selectedTier,
        returnUrl: `${window.location.origin}${window.location.pathname}`,
      });

      const checkoutUrl = typeof data === "string" ? data : data?.url;
      if (checkoutUrl && checkoutUrl.startsWith("http")) {
        if (checkoutTab) {
          checkoutTab.location.href = checkoutUrl;
        } else {
          // Popup blocked — fall back to same-tab redirect
          window.location.href = checkoutUrl;
          return;
        }
      } else if (checkoutTab) {
        checkoutTab.close();
      }
      setPhase("idle");
    } catch (error) {
      if (checkoutTab) checkoutTab.close();
      sendUserLogs({
        userId: user?.id,
        userEmail: user?.email,
        type: "errorCreateBrainCheckout",
        additionalData: error,
      });
      setPhase("idle");
    }
  };

  const handleCloseGetStarted = () => {
    dispatch({
      type: ReduxActionTypes.REMOVE_LAYOUT_ITEM,
      payload: {
        id: "Get Started",
        title: "Get Started",
        component: "Onboarding",
      },
    });
  };

  const handleBrowseMarketplace = () => {
    dispatch({
      type: ReduxActionTypes.SET_LAYOUT_ITEM,
      payload: {
        component: "TemplateManager",
        type: "TemplateManager",
        title: "Asset Marketplace",
      },
    });
  };

  const handleBrowseTools = () => {
    openServiceCreation("New service");
  };

  return (
    <Wrapper>
      {hasBrain ? (
        /* ── The org already has a server: its Brain is live ── */
        <HeroPanel>
          <LiveCard>
            <BrainStatus>
              <span className="dot" />
              <span>
                <span className="slug">{brainName}</span> ·{" "}
                <span className="ready">Online</span>
              </span>
            </BrainStatus>
            <HeroTitle>
              Your company <GradientText>Brain</GradientText> is live.
            </HeroTitle>
            <PitchLead style={{ maxWidth: 540, textAlign: "center" }}>
              It&apos;s already remembering your business, running your
              workflows and getting sharper every day. Keep growing it with more
              tools, agents and templates — all on the same server.
            </PitchLead>

            <BrainStage $intensity={3}>
              <SphereHalo $intensity={3} />
              <BrainSphere intensity={3} />
            </BrainStage>

            <LiveActions>
              <LivePrimary type="button" onClick={handleBrowseTools}>
                Grow your Brain →
              </LivePrimary>
              <GhostButton type="button" onClick={handleBrowseMarketplace}>
                Explore templates
              </GhostButton>
            </LiveActions>

            <CloseHint type="button" onClick={handleCloseGetStarted}>
              You&apos;re all set — close Get Started
            </CloseHint>
          </LiveCard>
        </HeroPanel>
      ) : (
        /* ── No server yet: create your Company Brain ── */
        <HeroPanel>
          <GlassCard>
            {/* Left: the living Brain */}
            <PitchColumn>
              <BrainStatus>
                <span className="dot" />
                <span>
                  <span className="slug">{displayName}</span> ·{" "}
                  {selectedTierData.ram} ·{selectedTierData.cpu} ·{" "}
                  <span className="ready">Ready to launch</span>
                </span>
              </BrainStatus>
              <HeroTitle>
                Meet your company&apos;s <GradientText>Brain.</GradientText>
              </HeroTitle>
              <PitchLead>
                An AI that knows your entire company, works alongside every team
                member 24/7, and gets smarter every single day.
              </PitchLead>

              {/* The Brain at the centre, its integrations orbiting around,
                  grouped by category */}
              <OrbitStage>
                <OrbitLines viewBox="0 0 100 100" preserveAspectRatio="none">
                  {ORBIT.flatMap(group =>
                    group.items.map(item => {
                      const active = hoveredNode === item.name;
                      return (
                        <line
                          key={`${group.category}-${item.name}`}
                          x1="50"
                          y1="50"
                          x2={item.x}
                          y2={item.y}
                          stroke={active ? "#4f46e5" : "rgba(99,102,241,0.2)"}
                          strokeWidth={active ? 0.7 : 0.35}
                          strokeDasharray="1.4 1.4"
                          vectorEffect="non-scaling-stroke"
                          style={{
                            transition: "stroke 0.18s, stroke-width 0.18s",
                          }}
                        />
                      );
                    }),
                  )}
                </OrbitLines>

                <OrbitCenter>
                  <SphereHalo $intensity={intensity} />
                  <BrainSphere intensity={intensity} />
                </OrbitCenter>

                {ORBIT.map(group => {
                  const groupActive = group.items.some(
                    it => it.name === hoveredNode,
                  );
                  return (
                    <React.Fragment key={group.category}>
                      <OrbitLabel
                        $active={groupActive}
                        style={{
                          left: `${group.labelX}%`,
                          top: `${group.labelY}%`,
                        }}
                      >
                        {group.category}
                      </OrbitLabel>
                      {group.items.map((item, i) => (
                        <OrbitNode
                          key={item.name}
                          type="button"
                          $dim={
                            hoveredNode !== null && hoveredNode !== item.name
                          }
                          aria-label={`${item.name} — ${item.desc}`}
                          onMouseEnter={() => setHoveredNode(item.name)}
                          onMouseLeave={() =>
                            setHoveredNode(h => (h === item.name ? null : h))
                          }
                          onFocus={() => setHoveredNode(item.name)}
                          onBlur={() =>
                            setHoveredNode(h => (h === item.name ? null : h))
                          }
                          style={{
                            left: `${item.x}%`,
                            top: `${item.y}%`,
                            animationDelay: `${i * 0.35}s`,
                          }}
                        >
                          <img src={item.logo} alt={item.name} />
                        </OrbitNode>
                      ))}
                    </React.Fragment>
                  );
                })}

                {ORBIT.flatMap(group => group.items)
                  .filter(item => item.name === hoveredNode)
                  .map(item => (
                    <OrbitTooltip
                      key={item.name}
                      style={{
                        left: `${item.x}%`,
                        top: `calc(${item.y}% + 30px)`,
                      }}
                    >
                      <div className="tip-head">
                        <img src={item.logo} alt="" />
                        <span className="tip-name">{item.name}</span>
                        <span className="tip-cat">{item.category}</span>
                      </div>
                      <div className="tip-desc">{item.desc}</div>
                    </OrbitTooltip>
                  ))}
              </OrbitStage>

              <div
                style={{
                  textAlign: "center",
                  fontSize: "11px",
                  color: "#94a3b8",
                  margin: "0 0 6px",
                }}
              >
                60+ built-in tools · MCP compatible · build your own
              </div>

              <PoweredBy>
                <span className="pb-lead">Powered by</span>
                <span className="pb-item">
                  <img
                    src="https://docs.openclaw.ai/assets/openclaw.svg"
                    alt="OpenClaw"
                  />
                  <span className="pb-name">
                    <strong>OpenClaw</strong>
                    <span className="pb-sub">Autonomous Agents</span>
                  </span>
                </span>
                <span className="sep">·</span>
                <span className="pb-item">
                  <img
                    src="https://hermes-agent.nousresearch.com/docs/img/logo.png"
                    alt="Hermes"
                  />
                  <span className="pb-name">
                    <strong>Hermes</strong>
                    <span className="pb-sub">Multi-Agent Runtime</span>
                  </span>
                </span>
              </PoweredBy>
            </PitchColumn>

            {/* Right: the config form */}
            <ConfigColumn>
              <FormDivider>
                <span className="label">Configure your brain</span>
              </FormDivider>

              {/* Name */}
              <div>
                <FieldLabel htmlFor="brain-name">Brain name</FieldLabel>
                <NameInput
                  id="brain-name"
                  type="text"
                  value={serviceName}
                  onChange={handleNameChange}
                  placeholder={defaultName}
                  spellCheck={false}
                  autoComplete="off"
                />
                <UrlHint>
                  <span className="prefix">https://</span>
                  <span className="slug">{displayName}</span>
                  <span className="prefix">.platform.ubos.tech</span>
                </UrlHint>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#94a3b8",
                    margin: "4px 0 20px",
                    lineHeight: 1.4,
                  }}
                ></p>
              </div>

              {/* Hardware tiers */}
              <FieldLabel>
                Choose your Brain size{" "}
                <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                  — monthly cost if running 24/7
                </span>
              </FieldLabel>
              <TiersGrid>
                {HARDWARE_TIERS.map(tier => (
                  <TierCard
                    key={tier.key}
                    $selected={selectedTier === tier.key}
                    role="radio"
                    aria-checked={selectedTier === tier.key}
                    tabIndex={0}
                    onClick={() => setSelectedTier(tier.key)}
                    onKeyDown={e =>
                      (e.key === "Enter" || e.key === " ") &&
                      setSelectedTier(tier.key)
                    }
                  >
                    {tier.recommended && (
                      <RecommendedBadge>Recommended</RecommendedBadge>
                    )}
                    <span className="tier-check">
                      {selectedTier === tier.key ? "✓" : ""}
                    </span>
                    <span className="tier-name">{tier.name}</span>
                    <div className="tier-tagline">{tier.tagline}</div>
                    <div className="tier-price">
                      <span className="amount">${tier.priceMonth}</span>
                      <span className="unit">/mo</span>
                    </div>
                    <ul className="tier-caps">
                      {tier.capabilities.map(cap => (
                        <li key={cap}>{cap}</li>
                      ))}
                    </ul>
                    <div className="tier-specs">
                      <div className="tier-spec">
                        <strong>RAM</strong>
                        {tier.ram}
                      </div>
                      <div className="tier-spec">
                        <strong>CPU</strong>
                        {tier.cpu}
                      </div>
                    </div>
                  </TierCard>
                ))}
              </TiersGrid>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                  margin: "0 0 20px",
                  padding: "12px 14px",
                  background: "rgba(248,250,252,0.9)",
                  borderRadius: "10px",
                  border: "1px solid rgba(226,232,240,0.8)",
                }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase" as const,
                    letterSpacing: "0.08em",
                    marginRight: "2px",
                  }}
                >
                  After launch:
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#475569",
                    fontWeight: 500,
                  }}
                >
                  Brain boots (~2 min)
                </span>
                <span style={{ fontSize: "12px", color: "#cbd5e1" }}>→</span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#475569",
                    fontWeight: 500,
                  }}
                >
                  Connect your tools
                </span>
                <span style={{ fontSize: "12px", color: "#cbd5e1" }}>→</span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#22c55e",
                    fontWeight: 700,
                  }}
                >
                  You&apos;re live
                </span>
              </div>

              {/* CTA */}
              <SetupButton
                type="button"
                onClick={handleSetupBrain}
                disabled={phase !== "idle"}
              >
                {phase === "redirecting"
                  ? "Opening secure checkout…"
                  : booting
                  ? "Creating your Company Brain…"
                  : `Launch My Company Brain →`}
              </SetupButton>
              <PayNote>
                You top up your balance to start — the Brain draws from it only
                while running. ${selectedTierData.priceMonth}/mo is the most
                you&apos;d pay at 24/7 usage; pause anytime and unused balance
                stays yours.
                <br />
                <span style={{ color: "#94a3b8" }}>
                  Pay-as-you-go · Secure via Stripe
                </span>
              </PayNote>
            </ConfigColumn>
          </GlassCard>

          {booting && (
            <BootOverlay>
              <BootOrb />
              <BootTitle>
                Creating <span className="slug">{displayName}</span>…
              </BootTitle>
              <BootLog>
                {bootLines.map((line, i) => {
                  if (i > bootStep) return null;
                  const isActive =
                    i === bootStep && bootStep < bootLines.length;
                  return (
                    <BootLine key={line} $active={isActive}>
                      <span className="mark">{isActive ? "▸" : "✓"}</span>
                      {line}
                    </BootLine>
                  );
                })}
              </BootLog>
            </BootOverlay>
          )}

          <ScrollHint>
            <span>See what you can add later</span>
            <span className="arrow">⌄</span>
          </ScrollHint>
        </HeroPanel>
      )}

      {/* ── Ecosystem teaser: Tools & Templates ── */}
      <ToolsPanel>
        <TeaserHead>
          <div className="kicker">Once your Brain is live</div>
          <div className="title">Grow it on the same server</div>
        </TeaserHead>

        <TeaserGrid>
          <TeaserCard
            role="button"
            tabIndex={0}
            onClick={handleBrowseTools}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && handleBrowseTools()
            }
          >
            <TeaserIconRow>
              {SERVICES.slice(0, 5).map(svc => (
                <span className="bubble" key={svc.key} title={svc.name}>
                  {svc.icon}
                </span>
              ))}
              <span className="count">{SERVICES.length}+ tools</span>
            </TeaserIconRow>
            <div className="t-title">Add tools &amp; services</div>
            <div className="t-desc">
              Spin up Node-RED, PostgreSQL, MongoDB, dashboards and more —
              running right next to your Brain, no extra setup.
            </div>
            <span className="t-cta">Browse all tools →</span>
          </TeaserCard>

          <TeaserCard
            role="button"
            tabIndex={0}
            onClick={handleBrowseMarketplace}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && handleBrowseMarketplace()
            }
          >
            <TeaserIconRow>
              <span className="bubble">📦</span>
              <span className="bubble">🤖</span>
              <span className="bubble">🛒</span>
              <span className="bubble">📊</span>
              <span className="bubble">✨</span>
              <span className="count">Marketplace</span>
            </TeaserIconRow>
            <div className="t-title">Clone a template</div>
            <div className="t-desc">
              Start from ready-made apps and agents built by the community.
              Deploy a full solution in one click, then make it yours.
            </div>
            <span className="t-cta">Explore templates →</span>
          </TeaserCard>
        </TeaserGrid>
      </ToolsPanel>
    </Wrapper>
  );
};

export default OnboardingPage;
